import * as React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import Loader from "../../../../components/ui/Loader";
import { AmbassadorMonthlyStatsResponse } from "../../../../services/administration/ambassadorService";

interface AmbassadorChartProps {
  stats: AmbassadorMonthlyStatsResponse | null;
  selectedYear: number;
  availableYears: number[];
  loading: boolean;
  onYearChange: (year: number) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(value);

const CHART_LABELS: Record<string, string> = {
  revenus_izi: "Revenus IZI",
  referral_cost: "Bonus parrainage",
  salary_paid: "Salaires influenceurs",
  total_cost: "Coût total",
  profit_net: "Profit net",
  new_invites: "Nouveaux invités",
  new_active: "Nouveaux actifs",
};

export const AmbassadorChart: React.FC<AmbassadorChartProps> = ({
  stats,
  selectedYear,
  availableYears,
  loading,
  onYearChange,
}) => {
  const years = availableYears.length > 0 ? availableYears : [selectedYear];
  const chartData = stats?.months || [];
  const networkTotals = stats?.totals;

  const yearTotals = chartData.reduce(
    (acc, m) => ({
      revenus_izi: acc.revenus_izi + (m.revenus_izi || 0),
      salary_paid: acc.salary_paid + (m.salary_paid || 0),
      referral_cost: acc.referral_cost + (m.referral_cost || 0),
      total_cost: acc.total_cost + (m.total_cost || 0),
      profit_net: acc.profit_net + (m.profit_net || 0),
    }),
    { revenus_izi: 0, salary_paid: 0, referral_cost: 0, total_cost: 0, profit_net: 0 }
  );

  return (
    <div className="p-6 bg-white rounded-xl border space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h2 className="text-lg font-semibold text-gray-800">Statistiques mensuelles</h2>
        <div className="flex items-center gap-2">
          <label htmlFor="year-select" className="text-sm text-gray-600">
            Année
          </label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={(e) => onYearChange(Number(e.target.value))}
            className="px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-green-500"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {networkTotals && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
            <p className="text-sm text-gray-600">Ambassadeurs</p>
            <p className="text-2xl font-bold text-indigo-700">{networkTotals.total_ambassadors}</p>
          </div>
          <div className="p-4 bg-teal-50 rounded-lg border border-teal-100">
            <p className="text-sm text-gray-600">Filleuls (total invités)</p>
            <p className="text-2xl font-bold text-teal-700">{networkTotals.total_filleuls}</p>
          </div>
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
            <p className="text-sm text-gray-600">Filleuls actifs</p>
            <p className="text-2xl font-bold text-emerald-700">{networkTotals.total_active_filleuls}</p>
          </div>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : chartData.length === 0 ? (
        <p className="text-sm text-gray-400">Aucune donnée pour {selectedYear}.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-gray-600">Revenus IZI ({selectedYear})</p>
              <p className="font-bold text-green-600">{formatCurrency(yearTotals.revenus_izi)}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <p className="text-gray-600">Salaires influenceurs</p>
              <p className="font-bold text-orange-600">{formatCurrency(yearTotals.salary_paid)}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-gray-600">Coût total</p>
              <p className="font-bold text-red-600">{formatCurrency(yearTotals.total_cost)}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-gray-600">Profit net</p>
              <p className="font-bold text-blue-600">{formatCurrency(yearTotals.profit_net)}</p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(value, name) => {
                  const num = Number(value ?? 0);
                  const key = String(name ?? "");
                  const label = CHART_LABELS[key] || key;
                  if (key === "new_invites" || key === "new_active") return [num, label];
                  return [formatCurrency(num), label];
                }}
                labelFormatter={(label) => `${label} ${selectedYear}`}
              />
              <Legend formatter={(value) => CHART_LABELS[value] || value} />
              <Bar dataKey="revenus_izi" name="revenus_izi" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="referral_cost" name="referral_cost" fill="#f97316" radius={[4, 4, 0, 0]} />
              <Bar dataKey="salary_paid" name="salary_paid" fill="#a855f7" radius={[4, 4, 0, 0]} />
              <Bar dataKey="total_cost" name="total_cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit_net" name="profit_net" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

export default AmbassadorChart;
